import { useMemo } from 'react'

import {
  BrokenImageOutlined,
  CheckCircleOutlineOutlined,
  ReportProblemOutlined,
  VideocamOffOutlined,
  WifiOffOutlined,
} from '@mui/icons-material'
import { Tooltip, Typography } from '@mui/material'
import { AlertSeverityEnum, SmepEvent, SmepEventHealthEnum, SmepEventStateEnum } from '@nativewaves/nw-cloud-sdk/smep'
import { ColumnDef } from '@tanstack/react-table'
import { format } from 'date-fns'
import { upperFirst } from 'lodash-es'
import { useTranslation } from 'react-i18next'

import { CopyId } from '@shared/components/Table/Fields'
import { DATE_TIME_FORMAT } from '@shared/utils/support'
import { IconButtonLink } from 'components/LinkedMUIComponents'
import {
  MediaSummary,
  NoSrcPlaceholderWrapper,
  StateVisualizer,
  TagSummary,
  Thumbnail,
} from 'domains/Workspaces/Events'
import { readFixtureId } from 'domains/Workspaces/Events/Event/ConfigEditor/model'
import { SortableHeader } from 'domains/Workspaces/Events/SortableHeader'
import { buildCameraSummary } from 'domains/Workspaces/Events/useCameraStreamNames'
import { AlertBadge } from 'layouts'
import { eventsOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Events'
import { eventMonitoringRoute } from 'pages/NWPlatform/Workspaces/Workspace/Events/Event'
import { getAlertSeverityColor } from 'utils'

export type UseColumnsProps = {
  /** Resolved camera stream names, by stream id — see `useCameraStreamNames`. */
  cameraStreamNames: Map<string, string>
}

export const useColumns = ({ cameraStreamNames }: UseColumnsProps) => {
  const { t } = useTranslation(['common', 'domain'])

  return useMemo<ColumnDef<SmepEvent>[]>(
    () => [
      {
        accessorFn: (row) => ({ src: row.thumbnail?.url, alt: row.name }),
        id: '_thumbnail',
        header: () => null,
        meta: {
          sizing: {
            width: 96,
            px: 2,
          },
          align: 'center',
        },
        cell: ({ getValue }) => {
          const { src, alt } = getValue<{ src?: string; alt: string }>()
          if (src) {
            return (
              <picture>
                <Thumbnail type="image/jpeg" data={src} aria-label={alt}>
                  <NoSrcPlaceholderWrapper>
                    <BrokenImageOutlined />
                  </NoSrcPlaceholderWrapper>
                </Thumbnail>
              </picture>
            )
          }
          return (
            <NoSrcPlaceholderWrapper>
              <VideocamOffOutlined />
            </NoSrcPlaceholderWrapper>
          )
        },
      },
      {
        accessorFn: (row) => row.name ?? t('noNameProvided'),
        id: '_name',
        header: () => <SortableHeader title={t('name')} sortProp="orderByName" />,
        meta: {
          sizing: {
            minWidth: 220,
          },
        },
        cell: ({ getValue }) => (
          <Typography variant="inherit" noWrap>
            {getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorFn: (row) => ({ state: row.state, health: row.health }),
        id: '_status',
        header: () => <SortableHeader title={t('domain:Workspace.Event.status')} sortProp="orderByState" />,
        meta: {
          sizing: {
            width: 130,
            px: 2,
          },
          align: 'center',
        },
        cell: (cell) => {
          const { state, health } = cell.getValue<{ state: SmepEventStateEnum; health: SmepEventHealthEnum }>()

          return (
            <StateVisualizer variant="text-sm" state={state} health={health}>
              {upperFirst(state.toLowerCase())}
            </StateVisualizer>
          )
        },
      },
      {
        accessorKey: 'alerts',
        header: t('domain:Workspace.Event.alerts'),
        meta: {
          sizing: {
            width: 80,
            px: 2,
          },
          align: 'center',
        },
        cell: (cell) => {
          const alerts = cell.getValue<SmepEvent['alerts']>()

          if (alerts) {
            const severityLevel = alerts.some((item) => item.severity === AlertSeverityEnum.High)
              ? AlertSeverityEnum.High
              : AlertSeverityEnum.Medium
            return alerts.length > 0 ? (
              <AlertBadge
                badgeContent={alerts.length}
                color={severityLevel === AlertSeverityEnum.High ? 'error' : 'warning'}
                overlap="circular"
              >
                <IconButtonLink
                  size="small"
                  from={eventsOverviewRoute.fullPath}
                  to={eventMonitoringRoute.to}
                  params={{ eventId: cell.row.original.id }}
                >
                  <ReportProblemOutlined
                    sx={{
                      color: (theme) => getAlertSeverityColor(severityLevel, theme.palette.mode),
                    }}
                  />
                </IconButtonLink>
              </AlertBadge>
            ) : (
              <CheckCircleOutlineOutlined
                sx={{
                  color: (theme) => theme.palette.success[theme.palette.mode],
                }}
              />
            )
          } else {
            return (
              <Tooltip title={t('domain:Workspace.Event.eventAlertsNotLoaded')}>
                <WifiOffOutlined
                  sx={{
                    color: (theme) => theme.palette.info[theme.palette.mode],
                  }}
                />
              </Tooltip>
            )
          }
        },
      },
      {
        accessorKey: 'startTime',
        header: () => <SortableHeader title={t('domain:Workspace.Event.startTime')} sortProp="orderByStartTime" />,
        meta: {
          sizing: {
            minWidth: 150,
            px: 1,
          },
        },
        cell: ({ getValue }) => format(new Date(getValue<string>()), DATE_TIME_FORMAT),
      },
      {
        accessorKey: 'endTime',
        header: () => <SortableHeader title={t('domain:Workspace.Event.endTime')} sortProp="orderByEndTime" />,
        meta: {
          sizing: {
            minWidth: 150,
          },
        },
        cell: ({ getValue }) =>
          getValue<string>() ? format(new Date(getValue<string>()), DATE_TIME_FORMAT) : 'Open end',
      },
      {
        // The stream is what identifies an angle here — camera names are often left empty.
        id: '_cameras',
        header: t('domain:Workspace.Event.ConfigEditor.cameras'),
        meta: {
          sizing: {
            minWidth: 160,
            px: 1,
          },
        },
        cell: ({ row }) => <MediaSummary kind="video" items={buildCameraSummary(row.original, cameraStreamNames)} />,
      },
      {
        accessorFn: (row) => Array.from(row.config?.tags ?? []),
        id: '_tags',
        header: t('domain:Workspace.Event.tags'),
        meta: {
          sizing: {
            minWidth: 160,
            px: 1,
          },
        },
        cell: ({ getValue }) => <TagSummary tags={getValue<string[]>()} />,
      },
      {
        accessorFn: (row) => (row.config ? readFixtureId(row.config) : ''),
        id: '_fixtureId',
        header: t('domain:Workspace.Event.fixtureId'),
        meta: {
          sizing: {
            minWidth: 130,
            px: 1,
          },
        },
        cell: ({ getValue }) => (
          <Typography variant="inherit" noWrap>
            {getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'id',
        header: t('id'),
        meta: {
          sizing: {
            width: 170,
            px: 2,
          },
        },
        cell: ({ getValue }) => <CopyId id={getValue<string>()} />,
      },
    ],
    [cameraStreamNames, t],
  )
}
